import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminSectionBaseComponent } from 'src/app/shared/admin-section-base';
import { ConfirmDeleteDialogComponent } from '../shared/confirm-delete-dialog/confirm-delete-dialog.component';
import {
  AdminApiService,
  SpecialEvent,
  SpecialEventAttendee,
  SpecialEventUserSuggestion,
} from 'src/app/shared/services/admin-api.service';

@Component({
  standalone: false,
  selector: 'app-special-events',
  templateUrl: './special-events.component.html',
  styleUrls: ['./special-events.component.scss'],
})
export class SpecialEventsComponent extends AdminSectionBaseComponent implements OnInit {
  specialEvents: SpecialEvent[] = [];
  specialEventsSearchTerm = '';
  specialEventForm: Partial<SpecialEvent> = {};
  private originalSpecialEventForm: Partial<SpecialEvent> = {};
  editingSpecialEventId: number | null = null;
  creatingSpecialEvent = false;

  private eventDateCache: Record<'scheduled_start_dt' | 'scheduled_end_dt' | 'start_ts' | 'end_ts', { raw: string | null; date: Date | null }> = {
    scheduled_start_dt: { raw: null, date: null },
    scheduled_end_dt: { raw: null, date: null },
    start_ts: { raw: null, date: null },
    end_ts: { raw: null, date: null },
  };

  specialEventAttendees: SpecialEventAttendee[] = [];
  attendeeSearchTerm = '';
  attendeeForm: Partial<SpecialEventAttendee> = {};
  private originalAttendeeForm: Partial<SpecialEventAttendee> = {};
  editingAttendeeEmail: string | null = null;
  creatingAttendee = false;

  attendeeUserSuggestions: SpecialEventUserSuggestion[] = [];
  private attendeeSuggestionTimer: any = null;
  attendeeImportBusy = false;

  constructor(
    private adminApiService: AdminApiService,
    private dialog: MatDialog,
    snackBar: MatSnackBar,
    cdr: ChangeDetectorRef,
  ) {
    super(snackBar, cdr);
  }

  ngOnInit(): void {
    this.refreshSpecialEvents();
  }

  get isSpecialEventEditorVisible(): boolean {
    return this.creatingSpecialEvent || this.editingSpecialEventId !== null;
  }

  get isSpecialEventFormDirty(): boolean {
    return JSON.stringify(this.specialEventForm) !== JSON.stringify(this.originalSpecialEventForm);
  }

  refreshSpecialEvents(): void {
    this.trackApiCall(this.adminApiService.listSpecialEvents(this.token, this.specialEventsSearchTerm)).subscribe({
      next: (events) => { this.specialEvents = events; },
      error: (error) => this.handleError(error),
    });
  }

  onSpecialEventsSearchTermChange(): void {
    this.refreshSpecialEvents();
  }

  openCreateSpecialEvent(): void {
    this.cancelSpecialEventEdit();
    this.creatingSpecialEvent = true;
    this.specialEventForm = {};
    this.originalSpecialEventForm = {};
  }

  openEditSpecialEvent(item: SpecialEvent): void {
    this.creatingSpecialEvent = false;
    this.editingSpecialEventId = item.event_id;
    this.specialEventForm = { ...item };
    this.originalSpecialEventForm = { ...item };
    this.loadAttendees(item.event_id);
  }

  cancelSpecialEventEdit(): void {
    this.editingSpecialEventId = null;
    this.creatingSpecialEvent = false;
    this.specialEventForm = {};
    this.originalSpecialEventForm = {};
    this.specialEventAttendees = [];
    this.attendeeSearchTerm = '';
    this.cancelAttendeeEdit();
  }

  saveSpecialEvent(): void {
    if (!this.specialEventForm.event_name || !this.specialEventForm.event_name.trim()) {
      this.showError('Event name is required.');
      return;
    }

    const { event_id: _ignored, ...formWithoutId } = this.specialEventForm;
    const payload: Partial<SpecialEvent> = {
      ...formWithoutId,
      event_name: (formWithoutId.event_name || '').trim(),
      sponsor_email: this.toNullableString(formWithoutId.sponsor_email),
      description: this.toNullableString(formWithoutId.description),
      country: this.toNullableString(formWithoutId.country),
      city: this.toNullableString(formWithoutId.city),
      scheduled_start_dt: this.toNullableString(formWithoutId.scheduled_start_dt),
      scheduled_end_dt: this.toNullableString(formWithoutId.scheduled_end_dt),
      start_ts: this.toNullableString(formWithoutId.start_ts),
      end_ts: this.toNullableString(formWithoutId.end_ts),
      sponsoring_td_org: this.toNullableString(formWithoutId.sponsoring_td_org),
    };

    const request = this.editingSpecialEventId
      ? this.adminApiService.updateSpecialEvent(this.token, this.editingSpecialEventId, payload)
      : this.adminApiService.createSpecialEvent(this.token, payload);

    const actionKey = this.specialEventSaveActionKey();

    this.trackActionCall(actionKey, request).subscribe({
      next: (saved) => {
        this.showSuccess(`Special event ${this.editingSpecialEventId ? 'updated' : 'created'} successfully.`);
        if (!this.editingSpecialEventId && saved) {
          this.openEditSpecialEvent(saved);
        } else {
          this.originalSpecialEventForm = { ...this.specialEventForm };
        }
        this.refreshSpecialEvents();
      },
      error: (error) => this.handleError(error),
    });
  }

  deleteSpecialEvent(item: SpecialEvent): void {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '400px',
      maxWidth: '94vw',
      autoFocus: false,
      panelClass: 'app-dialog-panel',
      data: {
        title: 'Delete Special Event',
        message: 'Are you sure you want to permanently delete this special event?',
        itemName: item.event_name || `Event ${item.event_id}`
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        const actionKey = this.specialEventDeleteActionKey(item.event_id);
        this.trackActionCall(actionKey, this.adminApiService.deleteSpecialEvent(this.token, item.event_id)).subscribe({
          next: () => {
            this.showSuccess('Special event deleted successfully.');
            if (this.editingSpecialEventId === item.event_id) {
              this.cancelSpecialEventEdit();
            }
            this.refreshSpecialEvents();
          },
          error: (error) => this.handleError(error),
        });
      }
    });
  }

  // ----- Attendees -----

  loadAttendees(eventId: number): void {
    this.trackApiCall(this.adminApiService.listSpecialEventAttendees(this.token, eventId, this.attendeeSearchTerm)).subscribe({
      next: (attendees) => { this.specialEventAttendees = attendees; },
      error: (error) => this.handleError(error),
    });
  }

  onAttendeeSearchTermChange(): void {
    if (this.editingSpecialEventId) {
      this.loadAttendees(this.editingSpecialEventId);
    }
  }

  openCreateAttendee(): void {
    this.cancelAttendeeEdit();
    this.creatingAttendee = true;
    this.attendeeForm = { attendee_type: 'A' };
    this.originalAttendeeForm = {};
  }
  openEditAttendee(item: SpecialEventAttendee): void {
    this.creatingAttendee = false;
    this.editingAttendeeEmail = item.email;
    this.attendeeForm = { ...item };
    this.originalAttendeeForm = { ...item };
  }

  cancelAttendeeEdit(): void {
    if (this.attendeeSuggestionTimer) {
      clearTimeout(this.attendeeSuggestionTimer);
      this.attendeeSuggestionTimer = null;
    }
    this.editingAttendeeEmail = null;
    this.creatingAttendee = false;
    this.attendeeForm = {};
    this.originalAttendeeForm = {};
    this.attendeeUserSuggestions = [];
  }

  saveAttendee(): void {
    if (!this.editingSpecialEventId) {
      this.showError('Open a special event first.');
      return;
    }
    if (!this.attendeeForm.email || !this.attendeeForm.email.trim()) {
      this.showError('Attendee email is required.');
      return;
    }

    const eventId = this.editingSpecialEventId;
    const email = this.attendeeForm.email.trim();

    const payload: Partial<SpecialEventAttendee> = {
      email,
      event_attendee_name: this.toNullableString(this.attendeeForm.event_attendee_name),
      company: this.toNullableString(this.attendeeForm.company),
      start_ts: this.toNullableString(this.attendeeForm.start_ts),
      end_ts: this.toNullableString(this.attendeeForm.end_ts),
      attendee_type: this.attendeeForm.attendee_type || null,
    };

    const request = this.editingAttendeeEmail
      ? this.adminApiService.updateSpecialEventAttendee(this.token, eventId, this.editingAttendeeEmail, payload)
      : this.adminApiService.createSpecialEventAttendee(this.token, eventId, payload);

    const actionKey = this.attendeeSaveActionKey();

    this.trackActionCall(actionKey, request).subscribe({
      next: () => {
        this.showSuccess(`Attendee ${this.editingAttendeeEmail ? 'updated' : 'added'} successfully.`);
        this.cancelAttendeeEdit();
        this.loadAttendees(eventId);
      },
      error: (error) => this.handleError(error),
    });
  }

  deleteAttendee(item: SpecialEventAttendee): void {
    if (!this.editingSpecialEventId || !item.email) {
      return;
    }
    const eventId = this.editingSpecialEventId;
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '400px',
      maxWidth: '94vw',
      autoFocus: false,
      panelClass: 'app-dialog-panel',
      data: {
        title: 'Delete Attendee',
        message: 'Are you sure you want to permanently delete this attendee?',
        itemName: item.email || 'Attendee'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        const actionKey = this.attendeeDeleteActionKey(item.email!);
        this.trackActionCall(actionKey, this.adminApiService.deleteSpecialEventAttendee(this.token, eventId, item.email!)).subscribe({
          next: () => {
            this.showSuccess('Attendee deleted successfully.');
            if (this.editingAttendeeEmail === item.email) {
              this.cancelAttendeeEdit();
            }
            this.loadAttendees(eventId);
          },
          error: (error) => this.handleError(error),
        });
      }
    });
  }

  specialEventSaveActionKey(): string {
    return 'special-event-save';
  }

  specialEventDeleteActionKey(eventId: number): string {
    return `special-event-delete:${eventId}`;
  }

  attendeeSaveActionKey(): string {
    return 'attendee-save';
  }

  attendeeDeleteActionKey(email: string): string {
    return `attendee-delete:${email}`;
  }

  attendeeTypeLabel(type: string | null): string {
    switch ((type || '').toUpperCase()) {
      case 'A': return 'Attendee';
      case 'S': return 'Sponsor';
      case 'O': return 'Observer';
      default: return '';
    }
  }

  onAttendeeNameChange(term: string | SpecialEventUserSuggestion | null): void {
    if (this.attendeeSuggestionTimer) {
      clearTimeout(this.attendeeSuggestionTimer);
    }

    // Autocomplete selection can write the full object into ngModel; only search when the user is typing.
    if (term && typeof term !== 'string') {
      return;
    }

    const query = (term || '').trim();
    if (query.length < 2) {
      this.attendeeUserSuggestions = [];
      return;
    }

    this.attendeeSuggestionTimer = setTimeout(() => {
      this.adminApiService.searchSpecialEventUsers(this.token, query).subscribe({
        next: (users) => { this.attendeeUserSuggestions = users; },
        error: () => { this.attendeeUserSuggestions = []; },
      });
    }, 250);
  }

  displayAttendeeSuggestion(suggestion: SpecialEventUserSuggestion | string | null): string {
    if (!suggestion) {
      return '';
    }
    return typeof suggestion === 'string' ? suggestion : suggestion.name;
  }

  selectAttendeeSuggestion(suggestion: SpecialEventUserSuggestion): void {
    this.attendeeForm.event_attendee_name = suggestion.name;
    if (!this.editingAttendeeEmail) {
      this.attendeeForm.email = suggestion.email;
    }
    if (suggestion.company) {
      this.attendeeForm.company = suggestion.company;
    }
    this.attendeeUserSuggestions = [];
  }

  onAttendeeFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;
    if (!file) {
      return;
    }
    if (!this.editingSpecialEventId) {
      this.showError('Open a special event first.');
      input.value = '';
      return;
    }

    const eventId = this.editingSpecialEventId;
    this.attendeeImportBusy = true;
    this.adminApiService.bulkUploadSpecialEventAttendees(this.token, eventId, file).subscribe({
      next: (result) => {
        this.attendeeImportBusy = false;
        input.value = '';
        this.showSuccess(`Import complete: ${result.inserted} added, ${result.updated} updated, ${result.skipped} skipped.`);
        this.loadAttendees(eventId);
      },
      error: (error) => {
        this.attendeeImportBusy = false;
        input.value = '';
        this.handleError(error);
      },
    });
  }

  getEventDate(field: 'scheduled_start_dt' | 'scheduled_end_dt' | 'start_ts' | 'end_ts'): Date | null {
    const value = this.specialEventForm[field];
    if (!value) {
      this.eventDateCache[field] = { raw: null, date: null };
      return null;
    }

    const cached = this.eventDateCache[field];
    if (cached && cached.raw === value) {
      return cached.date;
    }

    const raw = String(value);
    const parsed = new Date(raw.includes(' ') ? raw.replace(' ', 'T') : `${raw}T00:00:00`);
    const date = isNaN(parsed.getTime()) ? null : parsed;
    this.eventDateCache[field] = { raw: value, date };
    return date;
  }

  setEventDate(field: 'scheduled_start_dt' | 'scheduled_end_dt' | 'start_ts' | 'end_ts', value: Date | null): void {
    if (!value) {
      this.specialEventForm[field] = null;
      this.eventDateCache[field] = { raw: null, date: null };
      return;
    }
    const pad = (n: number): string => String(n).padStart(2, '0');
    const datePart = `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
    const stored = (field === 'start_ts' || field === 'end_ts')
      ? `${datePart} 00:00:00`
      : datePart;
    this.specialEventForm[field] = stored;
    this.eventDateCache[field] = { raw: stored, date: value };
  }
}
